import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { api } from '../apiConfig';
import { endpoints } from '../apiConfig';
import styles from './ExhibitionManager.module.css';
import dashboardStyles from '../pages/Dashboard.module.css';
import ExhibitionPhotos from './ExhibitionPhotos';
import ExhibitionPhotoCarousel from './ExhibitionPhotoCarousel';
import { uploadExhibitionPhotos } from '../utils/exhibitionPhotos';

import { artworkImage, coverPhoto, exhibitionCoverImage } from '../utils/exhibitionImages';
import { exhibitionPublicUrl } from '../utils/exhibitionLinks';
import { copyText } from '../utils/clipboard';

const emptyExhibition = {
  title: '',
  description: '',
  start_date: '',
  end_date: '',
  location: '',
  status: 'draft'
};

const STATUS_LABELS = {
  approved: 'Approved',
  own: 'Your artwork',
  draft: 'Preview only',
  pending: 'Pending artist approval',
  declined: 'Declined'
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const summarize = (exhibition) => {
  const invitations = exhibition.invitations || [];
  return {
    live: (exhibition.artworks || []).length,
    drafts: invitations.filter((invitation) => invitation.status === 'draft'),
    pending: invitations.filter((invitation) => invitation.status === 'pending'),
    declined: invitations.filter((invitation) => invitation.status === 'declined')
  };
};

const ExhibitionManager = ({ isHouseGallery = false }) => {
  const [exhibitions, setExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExhibition, setSelectedExhibition] = useState(null);
  const [newExhibition, setNewExhibition] = useState(emptyExhibition);
  const [availableArtworks, setAvailableArtworks] = useState([]);
  const [incomingInvitations, setIncomingInvitations] = useState([]);
  const [curatingId, setCuratingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewIncludesPending, setPreviewIncludesPending] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingCoverIndex, setPendingCoverIndex] = useState(0);
  const [copiedId, setCopiedId] = useState(null);

  const curating = exhibitions.find((exhibition) => exhibition.id === curatingId) || null;
  const previewing = exhibitions.find((exhibition) => exhibition.id === previewId) || null;

  useEffect(() => {
    fetchExhibitions();
    fetchAvailableArtworks();
    fetchIncomingInvitations();
  }, []);

  useEffect(() => {
    if (!curatingId || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return undefined;
    }

    const timeout = setTimeout(async () => {
      try {
        const response = await api.get(`/exhibitions/published-artworks?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(response.data || []);
      } catch (err) {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery, curatingId]);

  const anyModalOpen = showCreateModal || showEditModal || Boolean(curatingId) || Boolean(previewId);
  useEffect(() => {
    if (!anyModalOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [anyModalOpen]);

  const fetchExhibitions = async () => {
    try {
      const response = await api.get(endpoints.exhibitions.my);
      setExhibitions(response.data);
      return response.data;
    } catch (err) {
      setError('Failed to fetch exhibitions');
      console.error('Error fetching exhibitions:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomingInvitations = async () => {
    try {
      const response = await api.get('/exhibitions/invitations');
      setIncomingInvitations(response.data || []);
    } catch (err) {
      console.error('Error fetching exhibition invitations:', err);
    }
  };

  const fetchAvailableArtworks = async () => {
    try {
      const response = await api.get(endpoints.exhibitions.availableArtworks);
      setAvailableArtworks(response.data || []);
    } catch (err) {
      console.error('Error fetching artworks for exhibitions:', err);
    }
  };

  const handleCreateExhibition = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.post(endpoints.exhibitions.create, newExhibition);
      let photosFailed = false;
      if (pendingPhotos.length > 0) {
        try {
          await uploadExhibitionPhotos(response.data.id, pendingPhotos, pendingCoverIndex);
        } catch (photoError) {
          photosFailed = true;
          toast.error(photoError.response?.data?.error || 'The exhibition was saved, but the photos did not upload. Add them from Edit details.', { autoClose: 8000 });
        }
      }
      setShowCreateModal(false);
      setNewExhibition(emptyExhibition);
      setPendingPhotos([]);
      setPendingCoverIndex(0);
      await fetchExhibitions();
      openArtworks(response.data.id);
      if (!photosFailed) {
        toast.success('Exhibition saved. Now choose the artworks that belong in it.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create exhibition');
      console.error('Error creating exhibition:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditExhibition = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(endpoints.exhibitions.update(selectedExhibition.id), {
        title: selectedExhibition.title,
        description: selectedExhibition.description,
        start_date: selectedExhibition.start_date,
        end_date: selectedExhibition.end_date,
        location: selectedExhibition.location,
        status: selectedExhibition.status
      });
      setShowEditModal(false);
      fetchExhibitions();
      toast.success('Exhibition updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update exhibition');
      console.error('Error updating exhibition:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePublishExhibition = async (exhibition) => {
    const { live, drafts, pending } = summarize(exhibition);
    const waiting = drafts.length + pending.length;
    if (waiting > 0) {
      const confirmed = window.confirm([
        'Publishing makes the exhibition details and photos public now.',
        `${plural(waiting, 'artwork')} from other artists ${waiting === 1 ? 'is' : 'are'} not approved yet and will stay hidden from visitors.`,
        drafts.length > 0 ? `${plural(drafts.length, 'preview-only piece')} still ${drafts.length === 1 ? 'needs' : 'need'} Request approval before the artist is asked.` : null,
        'Each piece appears on its own as soon as its artist approves. You will not need to publish again.',
        live > 0 ? `Visitors will see ${plural(live, 'approved artwork')} today.` : 'Visitors will not see any artworks until one is approved.'
      ].filter(Boolean).join('\n\n'));
      if (!confirmed) return;
    }

    try {
      const response = await api.put(endpoints.exhibitions.update(exhibition.id), {
        title: exhibition.title,
        description: exhibition.description,
        start_date: exhibition.start_date,
        end_date: exhibition.end_date,
        location: exhibition.location,
        status: 'published'
      });
      const published = { ...exhibition, ...response.data };
      setExhibitions((prev) => prev.map((item) => (item.id === published.id ? { ...item, ...response.data } : item)));
      fetchExhibitions();
      toast.success(
        <div>
          <strong>{published.title} is live.</strong>
          <div>
            {waiting > 0
              ? 'Pending artworks will appear as their artists approve them.'
              : 'Share it with your collectors.'}
          </div>
          <div className={styles.actions}>
            {liveLinks(published)}
          </div>
        </div>,
        { autoClose: 10000, closeOnClick: false }
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to publish exhibition');
      console.error('Error publishing exhibition:', err);
    }
  };

  const copyPublicLink = async (exhibition) => {
    try {
      await copyText(exhibitionPublicUrl(exhibition));
      setCopiedId(exhibition.id);
      setTimeout(() => setCopiedId((current) => (current === exhibition.id ? null : current)), 2000);
      toast.success('Link copied');
    } catch (err) {
      toast.error(`Could not copy the link. Copy it from here: ${exhibitionPublicUrl(exhibition)}`, { autoClose: 10000 });
    }
  };

  const liveLinks = (exhibition) => (
    <>
      <a
        className={styles.editButton}
        href={exhibitionPublicUrl(exhibition)}
        target="_blank"
        rel="noopener noreferrer"
      >
        View exhibition
      </a>
      <button type="button" className={styles.editButton} onClick={() => copyPublicLink(exhibition)}>
        {copiedId === exhibition.id ? 'Link copied' : 'Copy link'}
      </button>
    </>
  );

  const liveRow = (exhibition) => (
    <div className={styles.liveRow}>
      <span className={`${styles.status} ${styles.published}`}>Published</span>
      {liveLinks(exhibition)}
    </div>
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (showEditModal) {
      setSelectedExhibition(prev => ({ ...prev, [name]: value }));
    } else {
      setNewExhibition(prev => ({ ...prev, [name]: value }));
    }
  };

  const openEditModal = (exhibition) => {
    setSelectedExhibition(exhibition);
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setNewExhibition(emptyExhibition);
    setPendingPhotos([]);
    setPendingCoverIndex(0);
    setShowCreateModal(true);
  };

  const applyPhotos = (exhibitionId, photos) => {
    const cover = coverPhoto(photos);
    const patch = (exhibition) => ({
      ...exhibition,
      photos,
      cover_image_url: cover?.image_url || null,
      image_url: cover ? cover.image_url : exhibition.image_url
    });
    setExhibitions((prev) => prev.map((exhibition) => (
      exhibition.id === exhibitionId ? patch(exhibition) : exhibition
    )));
    setSelectedExhibition((prev) => (prev?.id === exhibitionId ? patch(prev) : prev));
  };

  const openArtworks = (exhibitionId) => {
    setSearchQuery('');
    setSearchResults([]);
    setCuratingId(exhibitionId);
  };

  const openPreview = (exhibitionId) => {
    setPreviewIncludesPending(true);
    setPreviewId(exhibitionId);
  };

  const runAction = async (key, action) => {
    setBusyId(key);
    try {
      await action();
      await fetchExhibitions();
    } finally {
      setBusyId(null);
    }
  };

  const toggleExhibitionArtwork = (artwork, inExhibition) => runAction(artwork.id, async () => {
    try {
      if (inExhibition) {
        await api.delete(endpoints.exhibitions.removeArtwork(curating.id, artwork.id));
      } else {
        await api.post(endpoints.exhibitions.addArtwork(curating.id), { artwork_id: artwork.id });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update the exhibition');
    }
  });

  const addToPreview = (artwork) => runAction(artwork.id, async () => {
    try {
      const response = await api.post(`/exhibitions/${curating.id}/invitations`, {
        artwork_id: artwork.id,
        send: false
      });
      if (isHouseGallery) {
        toast.success(`${artwork.title} added to the exhibition`);
        return;
      }
      toast.success(response.data?.added
        ? `${artwork.title} added. ${artwork.artist_name} already approved your gallery.`
        : `${artwork.title} is in your preview. ${artwork.artist_name} has not been asked yet.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add that artwork');
    }
  });

  const askAgain = (invitation) => runAction(invitation.id, async () => {
    try {
      const response = await api.post(`/exhibitions/${curating.id}/invitations`, {
        artwork_id: invitation.artwork_id,
        send: true
      });
      toast.success(response.data?.emailSent
        ? `Request sent to ${invitation.artist_name}`
        : `Request saved for ${invitation.artist_name}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the request');
    }
  });

  const removeInvitation = (invitation) => runAction(invitation.id, async () => {
    try {
      await api.delete(`/exhibitions/${curating.id}/invitations/${invitation.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove that artwork');
    }
  });

  const requestApprovals = (exhibition) => runAction(`send-${exhibition.id}`, async () => {
    try {
      const response = await api.post(`/exhibitions/${exhibition.id}/invitations/send`);
      const { added, requested, artists, emailsSent } = response.data;
      if (added) {
        toast.success(`${plural(added, 'artwork')} added to the exhibition`);
        return;
      }
      toast.success(emailsSent > 0
        ? `Asked ${plural(artists, 'artist')} to approve ${plural(requested, 'artwork')}. They were emailed.`
        : `Saved ${plural(requested, 'request')}. The artists will see them on their dashboard.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the approval requests');
    }
  });

  const respondToInvitation = async (invitation, action) => {
    try {
      const response = await api.post(`/exhibitions/invitations/${invitation.id}/${action}`);
      toast.success(action === 'accept'
        ? (response.data?.emailSent
          ? `${invitation.inviter_name} was notified that you approved ${invitation.artwork_title}`
          : `You approved ${invitation.artwork_title}`)
        : `You declined ${invitation.artwork_title}`);
      fetchIncomingInvitations();
      fetchExhibitions();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update the invitation');
    }
  };

  // Everything currently attached to an exhibition, approved or not.
  const exhibitionItems = (exhibition) => {
    if (!exhibition) return [];
    const approved = (exhibition.artworks || []).map((artwork) => {
      const own = availableArtworks.some((item) => item.id === artwork.id && !item.represented);
      return {
        key: `artwork-${artwork.id}`,
        artworkId: artwork.id,
        title: artwork.title,
        artistName: own ? '' : artwork.artist_name,
        image: artworkImage(artwork),
        status: own ? 'own' : 'approved',
        artwork
      };
    });
    const guests = (exhibition.invitations || []).map((invitation) => ({
      key: `invitation-${invitation.id}`,
      artworkId: invitation.artwork_id,
      title: invitation.title,
      artistName: invitation.artist_name,
      image: invitation.image_url,
      status: invitation.status,
      invitation
    }));
    return [...approved, ...guests];
  };

  const curatingItems = exhibitionItems(curating);
  const attachedIds = new Set(curatingItems.map((item) => item.artworkId));
  const liveIds = new Set((curating?.artworks || []).map((artwork) => artwork.id));
  const ownArtworks = availableArtworks.filter((artwork) => !artwork.represented);
  const representedArtworks = availableArtworks.filter((artwork) => artwork.represented);
  const representedArtistIds = new Set(representedArtworks.map((artwork) => artwork.artist_id));
  const curatingSummary = curating ? summarize(curating) : null;

  const checklist = (artworks, emptyText) => (
    artworks.length === 0 ? (
      <span className={styles.helperText}>{emptyText}</span>
    ) : (
      <div className={styles.artworkPicker}>
        {artworks.map((artwork) => {
          const selected = liveIds.has(artwork.id);
          const image = artworkImage(artwork);
          return (
            <label
              key={artwork.id}
              className={`${styles.artworkOption} ${selected ? styles.artworkOptionSelected : ''}`}
            >
              <input
                type="checkbox"
                checked={selected}
                disabled={busyId === artwork.id}
                onChange={() => toggleExhibitionArtwork(artwork, selected)}
              />
              {image ? <img src={image} alt="" /> : <span className={styles.artworkPlaceholder} />}
              <span className={styles.artworkLabel}>
                {artwork.title}
                {artwork.represented && artwork.artist_name ? <small>{artwork.artist_name}</small> : null}
              </span>
            </label>
          );
        })}
      </div>
    )
  );

  const searchButton = (artwork) => {
    const attached = curatingItems.find((item) => item.artworkId === artwork.id);
    if (attached) {
      return <span className={styles.statusChip}>{STATUS_LABELS[attached.status]}</span>;
    }
    const represented = representedArtistIds.has(artwork.artist_id);
    return (
      <button
        type="button"
        className={styles.smallPrimary}
        disabled={busyId === artwork.id}
        onClick={() => addToPreview(artwork)}
      >
        {represented || isHouseGallery ? 'Add' : 'Add to preview'}
      </button>
    );
  };

  if (loading) return <div className={styles.loading}>Loading exhibitions...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  const previewItems = exhibitionItems(previewing).filter((item) => (
    item.status !== 'declined'
    && (previewIncludesPending || item.status === 'own' || item.status === 'approved')
  ));
  const previewHidden = exhibitionItems(previewing).filter((item) => item.status === 'draft' || item.status === 'pending').length;

  const detailsFields = (values, prefix) => (
    <>
      <div className={styles.formGroup}>
        <label htmlFor={`${prefix}title`}>Title</label>
        <input type="text" id={`${prefix}title`} name="title" value={values.title || ''} onChange={handleInputChange} required />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor={`${prefix}description`}>Description</label>
        <textarea id={`${prefix}description`} name="description" value={values.description || ''} onChange={handleInputChange} rows="3" required />
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor={`${prefix}start_date`}>Start Date</label>
          <input type="date" id={`${prefix}start_date`} name="start_date" value={values.start_date?.split('T')[0] || ''} onChange={handleInputChange} required />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor={`${prefix}end_date`}>End Date</label>
          <input type="date" id={`${prefix}end_date`} name="end_date" value={values.end_date?.split('T')[0] || ''} onChange={handleInputChange} required />
        </div>
      </div>
      <div className={styles.formGroup}>
        <label htmlFor={`${prefix}location`}>Location</label>
        <input type="text" id={`${prefix}location`} name="location" value={values.location || ''} onChange={handleInputChange} required />
      </div>
    </>
  );

  const photosIntro = (
    <div className={styles.sectionIntro}>
      <h4>Exhibition Photos</h4>
      <span className={styles.helperText}>
        Upload up to 20 installation or exhibition images. Choose a featured image to represent the exhibition. These are photos of the show, not artworks, so no artist approval is needed.
      </span>
    </div>
  );

  return (
    <section className={styles.container}>
      {incomingInvitations.length > 0 && (
        <div className={styles.invitationList}>
          <h3>Exhibition requests</h3>
          <p className={styles.helperText}>
            Someone wants to include your published work in their show. Approving does not change your price or who gets paid.
          </p>
          {incomingInvitations.map((invitation) => (
            <div key={invitation.id} className={styles.invitationRow}>
              <div>
                <strong>{invitation.artwork_title}</strong>
                <span>{invitation.inviter_name} · {invitation.exhibition_title}</span>
              </div>
              <div className={styles.invitationActions}>
                <button type="button" className={styles.cancelButton} onClick={() => respondToInvitation(invitation, 'decline')}>
                  Decline
                </button>
                <button type="button" className={styles.submitButton} onClick={() => respondToInvitation(invitation, 'accept')}>
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={dashboardStyles.sectionHeader}>
        <h2>Your Exhibitions</h2>
        <div className={dashboardStyles.sectionActions}>
          <button
            className={dashboardStyles.secondaryButton}
            onClick={openCreateModal}
          >
            Create New Exhibition
          </button>
        </div>
      </div>

      {exhibitions.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No exhibitions yet</h3>
          <p>Create a show, add your own work or other artists’ work, preview it, then publish.</p>
          <button className={styles.createButton} onClick={openCreateModal}>
            Create Exhibition
          </button>
        </div>
      ) : (
        <div className={styles.exhibitionGrid}>
          {exhibitions.map(exhibition => {
            const { live, drafts, pending, declined } = summarize(exhibition);
            const items = exhibitionItems(exhibition).filter((item) => item.status !== 'declined');
            const cardImage = exhibitionCoverImage(exhibition);
            const photoCount = (exhibition.photos || []).length;
            return (
              <div key={exhibition.id} className={styles.exhibitionCard}>
                {cardImage && (
                  <img src={cardImage} alt={exhibition.title} className={styles.exhibitionImage} />
                )}
                <div className={styles.exhibitionInfo}>
                  <h3>{exhibition.title}</h3>
                  <p className={styles.description}>{exhibition.description}</p>
                  <div className={styles.details}>
                    <span className={styles.date}>
                      {new Date(exhibition.start_date).toLocaleDateString()} - {new Date(exhibition.end_date).toLocaleDateString()}
                    </span>
                    <span className={styles.location}>{exhibition.location}</span>
                    {exhibition.status !== 'published' && (
                      <span className={`${styles.status} ${styles[exhibition.status]}`}>
                        {exhibition.status}
                      </span>
                    )}
                  </div>
                  {exhibition.status === 'published' && liveRow(exhibition)}
                  <p className={styles.artworkCount}>
                    {live + drafts.length + pending.length === 0
                      ? 'No artworks in this exhibition yet'
                      : [
                        live > 0 ? `${live} approved` : null,
                        drafts.length > 0 ? `${drafts.length} in preview only` : null,
                        pending.length > 0 ? `${pending.length} pending artist approval` : null,
                        declined.length > 0 ? `${declined.length} declined` : null
                      ].filter(Boolean).join(' · ')}
                    {photoCount > 0 ? ` · ${plural(photoCount, 'photo')}` : ''}
                  </p>
                  {items.length > 0 && (
                    <div className={styles.artworkStrip}>
                      {items.slice(0, 5).map((item) => (
                        item.image ? (
                          <img
                            key={item.key}
                            src={item.image}
                            alt={item.title}
                            className={`${styles.artworkThumb} ${item.status === 'draft' || item.status === 'pending' ? styles.artworkThumbPending : ''}`}
                          />
                        ) : null
                      ))}
                    </div>
                  )}
                  <div className={styles.actions}>
                    <button className={styles.editButton} onClick={() => openArtworks(exhibition.id)}>
                      {items.length === 0 ? 'Add artworks' : 'Manage artworks'}
                    </button>
                    {(items.length > 0 || photoCount > 0) && (
                      <button className={styles.editButton} onClick={() => openPreview(exhibition.id)}>
                        Preview
                      </button>
                    )}
                    <button className={styles.textButton} onClick={() => openEditModal(exhibition)}>
                      Edit details
                    </button>
                    {exhibition.status === 'draft' && (
                      <button className={styles.publishButton} onClick={() => handlePublishExhibition(exhibition)}>
                        Publish
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <div className={`${styles.modal} ae-dialog-scroll`}>
          <div className={`${styles.modalContent} ${styles.modalWide}`}>
            <h3>Create New Exhibition</h3>
            <form onSubmit={handleCreateExhibition}>
              <div className={styles.sectionIntro}>
                <h4>Exhibition Details</h4>
              </div>
              {detailsFields(newExhibition, '')}
              <div className={styles.curateSection}>
                {photosIntro}
                <ExhibitionPhotos
                  pendingFiles={pendingPhotos}
                  pendingCoverIndex={pendingCoverIndex}
                  onPendingChange={(files, cover) => {
                    setPendingPhotos(files);
                    setPendingCoverIndex(cover);
                  }}
                />
              </div>
              <div className={styles.curateSection}>
                <div className={styles.sectionIntro}>
                  <h4>Artworks in this Exhibition</h4>
                  <span className={styles.helperText}>
                    You choose the actual artworks on the next screen: your own work, artists who approved your gallery, and other artists’ published work.
                  </span>
                </div>
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelButton} onClick={() => setShowCreateModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className={styles.submitButton} disabled={saving}>
                  {saving ? 'Creating…' : 'Next: Add artworks'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedExhibition && (
        <div className={`${styles.modal} ae-dialog-scroll`}>
          <div className={`${styles.modalContent} ${styles.modalWide}`}>
            <h3>Edit Exhibition</h3>
            {selectedExhibition.status === 'published' && liveRow(selectedExhibition)}
            <form onSubmit={handleEditExhibition}>
              <div className={styles.sectionIntro}>
                <h4>Exhibition Details</h4>
              </div>
              {detailsFields(selectedExhibition, 'edit-')}
              <div className={styles.curateSection}>
                {photosIntro}
                <ExhibitionPhotos
                  exhibitionId={selectedExhibition.id}
                  photos={selectedExhibition.photos || []}
                  onPhotosChange={(photos) => applyPhotos(selectedExhibition.id, photos)}
                />
              </div>
              <div className={styles.curateSection}>
                <div className={styles.sectionIntro}>
                  <h4>Artworks in this Exhibition</h4>
                  <span className={styles.helperText}>
                    {(() => {
                      const { live, drafts, pending } = summarize(
                        exhibitions.find((exhibition) => exhibition.id === selectedExhibition.id) || selectedExhibition
                      );
                      const total = live + drafts.length + pending.length;
                      return total === 0
                        ? 'No artworks yet.'
                        : [
                          live > 0 ? `${live} approved` : null,
                          drafts.length > 0 ? `${drafts.length} in preview only` : null,
                          pending.length > 0 ? `${pending.length} pending artist approval` : null
                        ].filter(Boolean).join(' · ');
                    })()}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.smallSecondary}
                  onClick={() => openArtworks(selectedExhibition.id)}
                >
                  Manage artworks
                </button>
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelButton} onClick={() => setShowEditModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className={styles.submitButton} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {curating && (
        <div className={`${styles.modal} ${styles.stackedLayer} ae-dialog-scroll`}>
          <div className={`${styles.modalContent} ${styles.modalWide}`}>
            <h3>Artworks in {curating.title}</h3>
            <p className={styles.helperText}>
              {isHouseGallery
                ? 'Changes save as you go. Art Emerge exhibitions do not need artist approval, so any published work you add goes live with the exhibition.'
                : 'Changes save as you go. Work from other artists shows in your preview right away, but it only goes live after that artist approves it. Nobody is asked until you click Request approval.'}
            </p>

            <div className={styles.curateSection}>
              <h4>In this exhibition</h4>
              {curatingItems.length === 0 ? (
                <span className={styles.helperText}>Nothing yet. Choose your own work below, or search for another artist’s published work.</span>
              ) : (
                <div className={styles.itemList}>
                  {curatingItems.map((item) => (
                    <div key={item.key} className={styles.itemRow}>
                      {item.image ? <img src={item.image} alt="" /> : <span className={styles.artworkPlaceholder} />}
                      <span className={styles.artworkLabel}>
                        {item.title}
                        {item.artistName ? <small>{item.artistName}</small> : null}
                      </span>
                      <span className={`${styles.statusChip} ${styles[`chip_${item.status}`] || ''}`}>
                        {STATUS_LABELS[item.status]}
                      </span>
                      <div className={styles.itemActions}>
                        {item.status === 'declined' && (
                          <button type="button" className={styles.smallSecondary} disabled={busyId === item.invitation.id} onClick={() => askAgain(item.invitation)}>
                            Ask again
                          </button>
                        )}
                        <button
                          type="button"
                          className={styles.smallSecondary}
                          disabled={busyId === (item.invitation?.id || item.artworkId)}
                          onClick={() => (item.invitation
                            ? removeInvitation(item.invitation)
                            : toggleExhibitionArtwork(item.artwork, true))}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.curateSection}>
              <h4>Your artworks</h4>
              {checklist(ownArtworks, 'You have not uploaded any artwork yet. Upload work from your dashboard, or add other artists’ work below.')}
            </div>

            {representedArtworks.length > 0 && (
              <div className={styles.curateSection}>
                <h4>Artists who approved your gallery</h4>
                <span className={styles.helperText}>These artists already approved you, so their published work goes in without another request.</span>
                {checklist(representedArtworks, '')}
              </div>
            )}

            <div className={styles.curateSection}>
              <h4>Add another artist’s published work</h4>
              <span className={styles.helperText}>
                {isHouseGallery
                  ? 'Search by artwork title or artist name. The listing, price, and payout stay with the artist.'
                  : 'Search by artwork title or artist name. Pieces you add appear in your preview only. The listing, price, and payout stay with the artist.'}
              </span>
              <div className={styles.formGroup}>
                <input
                  type="search"
                  aria-label="Search published artwork"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                  placeholder="Search by artwork title or artist name"
                />
              </div>
              {searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <span className={styles.helperText}>No published artwork matches that search.</span>
              )}
              {searchResults.length > 0 && (
                <div className={styles.itemList}>
                  {searchResults.map((artwork) => (
                    <div key={artwork.id} className={styles.itemRow}>
                      {artwork.image_url ? <img src={artwork.image_url} alt="" /> : <span className={styles.artworkPlaceholder} />}
                      <span className={styles.artworkLabel}>
                        {artwork.title}
                        <small>{artwork.artist_name}</small>
                      </span>
                      <div className={styles.itemActions}>{searchButton(artwork)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelButton} onClick={() => setCuratingId(null)}>
                Done
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                disabled={attachedIds.size === 0}
                onClick={() => openPreview(curating.id)}
              >
                Preview exhibition
              </button>
              <button
                type="button"
                className={styles.submitButton}
                disabled={curatingSummary.drafts.length === 0 || busyId === `send-${curating.id}`}
                onClick={() => requestApprovals(curating)}
              >
                {curatingSummary.drafts.length === 0
                  ? 'Request approval'
                  : `Request approval (${curatingSummary.drafts.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewing && (
        <div className={`${styles.modal} ${styles.previewLayer} ae-dialog-scroll`}>
          <div className={`${styles.modalContent} ${styles.previewModal}`}>
            <div className={styles.previewBanner}>
              <strong>Preview</strong>
              <span>
                {previewing.status === 'published'
                  ? 'This exhibition is live. Work marked Preview only or Pending artist approval is shown here for you, but visitors only see approved work. Use View exhibition to see exactly what visitors see.'
                  : 'Only you can see this. The details and photos go public when you publish. Work marked Preview only or Pending artist approval stays hidden from visitors and appears on its own once the artist approves it.'}
              </span>
            </div>
            {(previewing.photos || []).length > 0 ? (
              <div className={styles.previewCarousel}>
                <ExhibitionPhotoCarousel photos={previewing.photos} title={previewing.title} />
              </div>
            ) : exhibitionCoverImage(previewing) && (
              <img src={exhibitionCoverImage(previewing)} alt={previewing.title} className={styles.previewHero} />
            )}
            <div className={styles.previewHeader}>
              <h3>{previewing.title}</h3>
              <p className={styles.previewMeta}>
                {new Date(previewing.start_date).toLocaleDateString()} – {new Date(previewing.end_date).toLocaleDateString()}
                {previewing.location ? ` · ${previewing.location}` : ''}
              </p>
              {previewing.description && <p className={styles.previewDescription}>{previewing.description}</p>}
            </div>
            <h4 className={styles.previewSubhead}>Artworks</h4>
            {previewHidden > 0 && (
              <label className={styles.previewToggle}>
                <input
                  type="checkbox"
                  checked={previewIncludesPending}
                  onChange={(event) => setPreviewIncludesPending(event.target.checked)}
                />
                Include {plural(previewHidden, 'artwork')} that {previewHidden === 1 ? 'is' : 'are'} not approved yet
              </label>
            )}
            {previewItems.length === 0 ? (
              <p className={styles.helperText}>No approved artwork yet. Visitors will see the exhibition details and photos, and each artwork appears once its artist approves it.</p>
            ) : (
              <div className={styles.previewGrid}>
                {previewItems.map((item) => (
                  <figure key={item.key} className={styles.previewItem}>
                    {item.image ? <img src={item.image} alt={item.title} /> : <span className={styles.previewPlaceholder} />}
                    <figcaption>
                      <strong>{item.title}</strong>
                      {item.artistName ? <span>{item.artistName}</span> : null}
                      {(item.status === 'draft' || item.status === 'pending') && (
                        <span className={`${styles.statusChip} ${styles[`chip_${item.status}`]}`}>
                          {STATUS_LABELS[item.status]}
                        </span>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelButton} onClick={() => setPreviewId(null)}>
                Close preview
              </button>
              {previewing.status === 'published' && liveLinks(previewing)}
              {summarize(previewing).drafts.length > 0 && (
                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={busyId === `send-${previewing.id}`}
                  onClick={() => requestApprovals(previewing)}
                >
                  Looks right. Request approval ({summarize(previewing).drafts.length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

ExhibitionManager.propTypes = {
  isHouseGallery: PropTypes.bool
};

export default ExhibitionManager;
